import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { useLocalContext } from "../../context/LocalContext";
import "./About.css";

const fadeUp = {
  hidden: { opacity: 0, y: 18 },
  show: { opacity: 1, y: 0, transition: { duration: 0.6 } },
};

const fade = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { duration: 0.6 } },
};

const About = () => {
  const { webinfo = {} } = useLocalContext();
  const supportEmail = webinfo?.email || "contact@smartprintersolutions.com";

  const trustStrip = [
    { label: "Focus", text: "Full-funnel digital" },
    { label: "Delivery", text: "Scoped proposals & SOWs" },
    { label: "Ethics", text: "No guaranteed ROI claims" },
    { label: "Support", text: "Accountable reporting" },
  ];

  const values = [
    {
      title: "Clarity over jargon",
      desc: "We explain channel tradeoffs in plain language so stakeholders can decide with confidence.",
    },
    {
      title: "Platform rules matter",
      desc: "Ad copy, landing pages, and data practices should align with network policies and applicable law.",
    },
    {
      title: "Measurement with humility",
      desc: "Attribution is imperfect. We label modeled data, sampling limits, and when a metric is directional only.",
    },
    {
      title: "Partnership, not dependency",
      desc: "We document access and assets so you can change agencies or go in-house without losing history.",
    },
  ];

  const capabilities = [
    "Marketing strategy, audits, and quarterly planning",
    "SEO: technical fixes, content briefs, and internal linking",
    "Paid search, paid social, and programmatic support where appropriate",
    "Organic social calendars, community playbooks, and creator coordination",
    "Email and lifecycle programs with list hygiene",
    "Analytics, tagging, dashboards, and conversion rate optimization tests",
  ];

  const process = [
    {
      step: "01",
      title: "Discover",
      desc: "We learn your offer, margins, sales cycle, and compliance boundaries before recommending channels.",
    },
    {
      step: "02",
      title: "Plan",
      desc: "We propose scope, budget, timeline, and success metrics—with explicit exclusions.",
    },
    {
      step: "03",
      title: "Execute",
      desc: "We build, launch, and QA campaigns, creative, and tracking alongside your approvals.",
    },
    {
      step: "04",
      title: "Improve",
      desc: "We review performance, test thoughtfully, and recommend when to scale, pause, or pivot.",
    },
  ];

  return (
    <section className="ab">
      <div className="ab__hero">
        <div className="ab__heroWrap">
          <motion.div
            className="ab__heroLeft"
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.4 }}
          >
            <p className="ab__kicker">About</p>
            <h1 className="ab__title">
              Digital marketing for <span>growth-minded teams</span>.
            </h1>
            <p className="ab__lead">
              smartprintersolutions.com provides strategy and hands-on execution across the channels that matter for customer
              acquisition and retention. We combine creativity with disciplined measurement—and we are upfront about what
              marketing can and cannot guarantee.
            </p>

            <div className="ab__heroCtas">
              <Link className="ab__btn ab__btn--primary" to="/contact">
                Start a conversation
              </Link>
              <a className="ab__btn ab__btn--ghost" href="#ab-work">
                See capabilities
              </a>
            </div>

            <div className="ab__heroBadges">
              <span className="ab__badge">Written scopes</span>
              <span className="ab__badge">Transparent fees</span>
              <span className="ab__badge">Privacy-aware tagging</span>
              <span className="ab__badge">Honest reporting</span>
            </div>
          </motion.div>

          <motion.div
            className="ab__heroRight"
            variants={fade}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.35 }}
          >
            <div className="ab__heroCard">
              <div className="ab__heroCardTop">
                <p className="ab__miniLabel">At a glance</p>
                <h2 className="ab__miniTitle">What we are (and are not)</h2>
              </div>

              <div className="ab__heroGrid">
                <div className="ab__pill">
                  <b>We are</b>
                  <span>Channel strategists &amp; operators</span>
                </div>
                <div className="ab__pill">
                  <b>We are not</b>
                  <span>A “guaranteed leads” shop</span>
                </div>
                <div className="ab__pill">
                  <b>We are</b>
                  <span>Aligned to your KPIs</span>
                </div>
                <div className="ab__pill">
                  <b>We are not</b>
                  <span>Your law firm or CPA</span>
                </div>
              </div>

              <div className="ab__heroProof">
                <div className="ab__proofItem">
                  <span className="ab__dot" />
                  <p>Contracts reference deliverables, access needs, and termination notice.</p>
                </div>
                <div className="ab__proofItem">
                  <span className="ab__dot" />
                  <p>We flag risky claims before they go live in paid or organic channels.</p>
                </div>
                <div className="ab__proofItem">
                  <span className="ab__dot" />
                  <p>We help you keep privacy policies aligned with real data collection.</p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        <div className="ab__heroGlow" aria-hidden="true" />
      </div>

      <div className="ab__section ab__section--tight">
        <div className="ab__wrap">
          <div className="ab__stats">
            {trustStrip.map((s) => (
              <motion.div
                key={s.label}
                className="ab__stat"
                variants={fadeUp}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, amount: 0.3 }}
              >
                <h3>{s.label}</h3>
                <p>{s.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      <div className="ab__section">
        <div className="ab__wrap ab__twoCol">
          <motion.div
            className="ab__card ab__card--soft"
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.3 }}
          >
            <p className="ab__eyebrow">Who we are</p>
            <h2 className="ab__h2">A focused marketing team.</h2>
            <p className="ab__p">
              We work with founders, marketing leads, and growing brands that want accountable digital programs—not vague
              retainers without outcomes discussion.
            </p>
            <p className="ab__p">
              Our experience spans B2B and B2C, but every engagement is unique. We validate fit before taking on work.
            </p>

            <div className="ab__highlights">
              <div className="ab__hl">
                <b>Strategy</b>
                <span>Positioning &amp; channel mix</span>
              </div>
              <div className="ab__hl">
                <b>Execution</b>
                <span>Campaigns, creative, tags</span>
              </div>
              <div className="ab__hl">
                <b>Insights</b>
                <span>Tests &amp; monthly reviews</span>
              </div>
            </div>
          </motion.div>

          <motion.div
            className="ab__card"
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.3 }}
          >
            <p className="ab__eyebrow" id="ab-work">
              Capabilities
            </p>
            <h2 className="ab__h2">What we can take on.</h2>
            <ul className="ab__list">
              {capabilities.map((c, i) => (
                <li key={i}>{c}</li>
              ))}
            </ul>

            <div className="ab__note">
              <b>Best fit:</b> Teams with budget to test, willingness to share data access, and decision-makers available
              for weekly or biweekly collaboration.
            </div>
          </motion.div>
        </div>
      </div>

      <div className="ab__section ab__section--muted">
        <div className="ab__wrap">
          <motion.div
            className="ab__head"
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.35 }}
          >
            <p className="ab__eyebrow">Values</p>
            <h2 className="ab__h2">How we try to work.</h2>
            <p className="ab__p ab__p--muted">Aspirational standards—we own mistakes and correct course quickly.</p>
          </motion.div>

          <div className="ab__grid4">
            {values.map((v) => (
              <motion.div
                key={v.title}
                className="ab__value"
                variants={fadeUp}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, amount: 0.25 }}
              >
                <h3 className="ab__h3">{v.title}</h3>
                <p className="ab__p ab__p--muted">{v.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      <div className="ab__section">
        <div className="ab__wrap">
          <motion.div
            className="ab__head"
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.35 }}
          >
            <p className="ab__eyebrow">Lifecycle</p>
            <h2 className="ab__h2">From first call to steady state.</h2>
            <p className="ab__p ab__p--muted">Phases flex, but the sequence keeps everyone aligned.</p>
          </motion.div>

          <div className="ab__process">
            {process.map((p) => (
              <motion.div
                key={p.step}
                className="ab__step"
                variants={fadeUp}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, amount: 0.25 }}
              >
                <div className="ab__stepNo">{p.step}</div>
                <div className="ab__stepBody">
                  <h3 className="ab__h3">{p.title}</h3>
                  <p className="ab__p ab__p--muted">{p.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      <div className="ab__section ab__section--tight">
        <div className="ab__wrap ab__twoCol">
          <motion.div
            className="ab__card ab__card--border"
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.3 }}
          >
            <p className="ab__eyebrow">Mission</p>
            <h2 className="ab__h2">Make marketing measurable and honest.</h2>
            <p className="ab__p ab__p--muted">
              Help clients invest in channels that fit their goals, with reporting that supports real decisions—not vanity
              charts.
            </p>
          </motion.div>

          <motion.div
            className="ab__card ab__card--border"
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.3 }}
          >
            <p className="ab__eyebrow">Vision</p>
            <h2 className="ab__h2">Trusted partner for growth teams.</h2>
            <p className="ab__p ab__p--muted">
              Be the agency clients recommend because we communicate clearly, document our work, and respect platform
              and privacy rules.
            </p>
          </motion.div>
        </div>
      </div>

      <div className="ab__cta" id="ab-contact">
        <div className="ab__wrap ab__ctaWrap">
          <motion.div
            className="ab__ctaCard"
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.35 }}
          >
            <h2 className="ab__ctaTitle">Tell us about your growth goals</h2>
            <p className="ab__ctaText">
              Share your site, current channels, and monthly budget range. We will reply with fit, options, and next
              steps—typically within one to two business days.
            </p>
            <div className="ab__ctaBtns">
              <Link className="ab__btn ab__btn--primary" to="/contact">
                Contact us
              </Link>
              <a className="ab__btn ab__btn--ghost" href={`mailto:${supportEmail}`}>
                Email us
              </a>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default About;
